<template>
  <!-- Consent-Banner: Bildschirm-Freigabe -->
  <Transition name="slide-up">
    <div v-if="phase === 'request'" class="ls-banner">
      <div class="ls-banner-icon">
        <img :src="themeStore.theme === 'dark' ? monitorWhite : monitorBlack" class="ls-banner-icon-img" alt="" />
      </div>
      <div class="ls-banner-body">
        <div class="ls-banner-title">Support-Anfrage von {{ adminName }}</div>
        <div class="ls-banner-sub">
          Der Administrator möchte deinen Bildschirm sehen, um dir zu helfen.
          Du kannst die Session jederzeit beenden.
        </div>
      </div>
      <div class="ls-banner-actions">
        <button class="ls-btn ls-btn--deny"   @click="deny">Ablehnen</button>
        <button class="ls-btn ls-btn--accept" @click="acceptView">Bildschirm freigeben</button>
      </div>
    </div>
  </Transition>

  <!-- Hinweis: Bildschirmfreigabe auf diesem Gerät nicht möglich -->
  <Transition name="slide-up">
    <div v-if="phase === 'unsupported'" class="ls-banner">
      <div class="ls-banner-icon">
        <img :src="themeStore.theme === 'dark' ? warningWhite : warningBlack" class="ls-banner-icon-img" alt="" />
      </div>
      <div class="ls-banner-body">
        <div class="ls-banner-title">Bildschirmfreigabe nicht verfügbar</div>
        <div class="ls-banner-sub">
          Dein Gerät bzw. Browser unterstützt leider keine Bildschirmfreigabe.
          Bitte nutze einen Desktop-Browser (z. B. Chrome, Edge oder Firefox).
        </div>
      </div>
      <div class="ls-banner-actions">
        <button class="ls-btn ls-btn--accept" @click="dismissUnsupported">Verstanden</button>
      </div>
    </div>
  </Transition>

  <!-- Consent-Banner: Cursor-Kontrolle -->
  <Transition name="slide-up">
    <div v-if="phase === 'control_request'" class="ls-banner ls-banner--control">
      <div class="ls-banner-icon">
        <img :src="themeStore.theme === 'dark' ? cursorWhite : cursorBlack" class="ls-banner-icon-img" alt="" />
      </div>
      <div class="ls-banner-body">
        <div class="ls-banner-title">Cursor-Kontrolle angefragt</div>
        <div class="ls-banner-sub">
          {{ adminName }} möchte einen Zeiger auf deinem Bildschirm anzeigen,
          um dir etwas zu erklären. Du behältst die volle Kontrolle.
        </div>
      </div>
      <div class="ls-banner-actions">
        <button class="ls-btn ls-btn--deny"   @click="denyControl">Ablehnen</button>
        <button class="ls-btn ls-btn--accept" @click="acceptControl">Zulassen</button>
      </div>
    </div>
  </Transition>

  <!-- Aktive Session: Status-Bar + Beenden-Button -->
  <Transition name="slide-up">
    <div v-if="phase === 'view' || phase === 'control'" class="ls-active-bar">
      <span class="ls-dot"></span>
      <span class="ls-active-label">
        {{ phase === 'control' ? 'Cursor-Kontrolle aktiv' : 'Bildschirm wird geteilt' }}
        – {{ adminName }}
      </span>
      <button class="ls-btn ls-btn--end" @click="endSession">Session beenden</button>
    </div>
  </Transition>

  <!-- Remote-Cursor-Overlay -->
  <Teleport to="body">
    <div v-if="phase === 'control' && cursorVisible"
         class="ls-remote-cursor"
         :style="{ left: cursorX * 100 + '%', top: cursorY * 100 + '%' }">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M4 2l16 8-8 2-4 8L4 2z" fill="var(--accent)" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>
      <span class="ls-cursor-label">{{ adminName }}</span>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { io } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth.js';
import { useThemeStore } from '@/stores/theme.js';
import api from '@/api/axios.js';
import monitorWhite from '@/assets/icons/monitor_white.png';
import monitorBlack from '@/assets/icons/monitor_black.png';
import warningWhite from '@/assets/icons/warning_white.png';
import warningBlack from '@/assets/icons/warning_black.png';
import cursorWhite from '@/assets/icons/cursor_white.png';
import cursorBlack from '@/assets/icons/cursor_black.png';

const auth       = useAuthStore();
const themeStore = useThemeStore();

// Phase: idle | request | view | control_request | control | ended
const phase         = ref('idle');
const adminName     = ref('Support');
const sessionToken  = ref(null);
const cursorX       = ref(0);
const cursorY       = ref(0);
const cursorVisible = ref(false);

const ADMIN_WS_URL = (import.meta.env.VITE_ADMIN_WS_URL || 'http://localhost:3010').replace(/\/$/, '');
const POLL_MS      = 5000;

let socket     = null;
let pc         = null;
let mediaStream = null;
let pollTimer  = null;

const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

// ─── Polling für ausstehende Sessions ────────────────────────────────────────

async function pollPending() {
  if (phase.value !== 'idle') return;
  try {
    const token = auth.token;
    if (!token) return;
    const { data } = await api.get('/live-session/pending', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (data.session && phase.value === 'idle') {
      sessionToken.value = data.session.session_token;
      adminName.value    = data.session.admin_name || 'Support';
      phase.value        = 'request';
    }
  } catch { /* ignore – non-critical */ }
}

// ─── Consent-Aktionen ─────────────────────────────────────────────────────────

// Beendet eine noch nicht angenommene Anfrage serverseitig (verhindert, dass
// das Banner durch erneutes Polling immer wieder erscheint) und informiert den Admin
async function notifyAdmin(reason) {
  if (!sessionToken.value) return;
  try {
    const token = auth.token;
    if (!token) return;
    await api.post('/live-session/respond',
      { session_token: sessionToken.value, reason },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch { /* ignore – non-critical */ }
}

async function deny() {
  socket?.emit('session:deny');
  phase.value = 'idle';
  disconnectSocket();
  await notifyAdmin('denied');   // erst beenden lassen, DANN Polling fortsetzen (verhindert Race Condition)
  restartPoll();
}

async function acceptView() {
  // Mobile Browser (iOS/Android) unterstützen getDisplayMedia i.d.R. nicht
  if (!navigator.mediaDevices?.getDisplayMedia) {
    phase.value = 'unsupported';
    return;
  }
  // getDisplayMedia MUSS synchron im User-Gesture-Kontext aufgerufen werden
  try {
    mediaStream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 15, max: 30 }, cursor: 'always' },
      audio: false,
    });
  } catch {
    return; // Nutzer hat abgebrochen – Banner bleibt, Session noch offen
  }
  phase.value = 'view';
  connectSocket();
}

async function dismissUnsupported() {
  phase.value = 'idle';
  disconnectSocket();
  await notifyAdmin('unsupported');   // erst beenden lassen, DANN Polling fortsetzen (verhindert Race Condition)
  restartPoll();
}

function denyControl() {
  socket?.emit('session:deny_control');
  phase.value = 'view';
}

function acceptControl() {
  socket?.emit('session:accept_control');
  phase.value = 'control';
}

function endSession() {
  socket?.emit('session:end');
  cleanup();
  restartPoll();
}

// ─── WebSocket ────────────────────────────────────────────────────────────────

function connectSocket() {
  socket = io(ADMIN_WS_URL, {
    path: '/live',
    auth: { type: 'tenant', session_token: sessionToken.value },
    transports: ['websocket', 'polling'],
  });

  socket.on('connect_error', () => {
    phase.value = 'idle';
    restartPoll();
  });

  socket.on('session:admin_connected', startWebRTC);

  socket.on('session:control_requested', () => {
    if (phase.value === 'view') phase.value = 'control_request';
  });

  socket.on('session:status', ({ status }) => {
    if (status === 'control') phase.value = 'control';
  });

  socket.on('session:ended', () => {
    cleanup();
    restartPoll();
  });

  socket.on('session:peer_disconnected', () => {
    cleanup();
    restartPoll();
  });

  // WebRTC: Admin schickt Answer
  socket.on('rtc:answer', async ({ sdp }) => {
    if (pc) await pc.setRemoteDescription(new RTCSessionDescription(sdp)).catch(() => {});
  });

  socket.on('rtc:ice', async ({ candidate }) => {
    if (pc && candidate) await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
  });

  // Cursor-Position vom Admin
  socket.on('cursor:move', ({ x, y }) => {
    if (x < 0 || y < 0) { cursorVisible.value = false; return; }
    cursorX.value       = x;
    cursorY.value       = y;
    cursorVisible.value = true;
  });
}

async function startWebRTC() {
  if (!mediaStream) { deny(); return; }

  socket.emit('session:accept_view');

  pc = new RTCPeerConnection(RTC_CONFIG);
  mediaStream.getTracks().forEach(t => pc.addTrack(t, mediaStream));

  pc.onicecandidate = ({ candidate }) => {
    if (candidate) socket.emit('rtc:ice', { candidate });
  };

  // Wenn Nutzer den nativen "Stopp teilen"-Button drückt
  mediaStream.getVideoTracks()[0].onended = () => endSession();

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  socket.emit('rtc:offer', { sdp: pc.localDescription });
}

// ─── Aufräumen ────────────────────────────────────────────────────────────────

function cleanup() {
  phase.value         = 'idle';
  cursorVisible.value = false;
  if (pc) { pc.close(); pc = null; }
  if (mediaStream) { mediaStream.getTracks().forEach(t => t.stop()); mediaStream = null; }
  disconnectSocket();
}

function disconnectSocket() {
  socket?.disconnect();
  socket = null;
}

function restartPoll() {
  clearTimeout(pollTimer);
  pollTimer = setTimeout(startPoll, 2000);
}

function startPoll() {
  clearInterval(pollTimer);
  pollPending();
  pollTimer = setInterval(pollPending, POLL_MS);
}

onMounted(startPoll);
onUnmounted(() => {
  clearInterval(pollTimer);
  cleanup();
});
</script>

<style scoped>
/* Banner (Anfrage-Dialoge) */
.ls-banner {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--bg-modal);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 8px 40px rgba(0,0,0,.35);
  max-width: 560px;
  width: calc(100vw - 48px);
}
.ls-banner--control { border-color: var(--accent-soft); }

.ls-banner-icon { font-size: 28px; flex-shrink: 0; display: flex; }
.ls-banner-icon-img { width: 28px; height: 28px; object-fit: contain; }
.ls-banner-body { flex: 1; min-width: 0; }
.ls-banner-title { font-weight: 700; font-size: 14px; margin-bottom: 3px; }
.ls-banner-sub   { font-size: 12px; color: var(--text-secondary); line-height: 1.4; }

.ls-banner-actions { display: flex; gap: 8px; flex-shrink: 0; }

/* Aktive Session-Bar */
.ls-active-bar {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 9998;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg-modal);
  color: var(--text-primary);
  padding: 8px 20px;
  font-size: 13px;
  border-bottom: 1px solid var(--border);
}
.ls-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #22c55e;
  animation: ls-pulse 1.5s infinite;
  flex-shrink: 0;
}
.ls-active-label { flex: 1; font-weight: 500; }

/* Buttons */
.ls-btn {
  border: none; border-radius: 8px;
  font-size: 13px; font-weight: 600;
  padding: 8px 14px; cursor: pointer;
  white-space: nowrap; transition: opacity .15s;
}
.ls-btn:hover          { opacity: .85; }
.ls-btn--accept        { background: var(--accent); color: #fff; }
.ls-btn--deny          { background: var(--bg-input); color: var(--text-secondary); }
.ls-btn--end           { background: rgba(239,68,68,.15); color: #f87171; border: 1px solid rgba(239,68,68,.3); margin-left: auto; }

/* Remote-Cursor */
.ls-remote-cursor {
  position: fixed;
  pointer-events: none;
  z-index: 99999;
  transform: translate(-2px, -2px);
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.ls-cursor-label {
  background: var(--accent);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 18px;
  white-space: nowrap;
}

/* Transitions */
.slide-up-enter-active, .slide-up-leave-active { transition: all .25s ease; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateX(-50%) translateY(16px); }
.slide-up-enter-to, .slide-up-leave-from { opacity: 1; transform: translateX(-50%) translateY(0); }

@keyframes ls-pulse { 0%,100% { opacity:1 } 50% { opacity:.4 } }

/* Mobile-Anpassungen */
@media (max-width: 640px) {
  .ls-banner {
    flex-direction: column;
    align-items: stretch;
    bottom: 12px;
    width: calc(100vw - 24px);
    padding: 14px 16px;
    gap: 10px;
  }
  .ls-banner-icon { font-size: 22px; }
  .ls-banner-icon-img { width: 22px; height: 22px; }
  .ls-banner-actions {
    gap: 8px;
  }
  .ls-banner-actions .ls-btn {
    flex: 1;
    text-align: center;
  }

  .ls-active-bar {
    flex-wrap: wrap;
    row-gap: 6px;
    padding: 8px 14px;
  }
  .ls-active-label {
    flex-basis: 100%;
    order: 1;
    white-space: normal;
  }
  .ls-btn--end {
    margin-left: 0;
  }
}
</style>
